import React, { useEffect, useState } from 'react';
import SEO from '../components/SEO';
import { auth } from '../firebase';
import '../styles/ExpoApp.css';

const ExpoApp = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hideHeader, setHideHeader] = useState(false);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setUser(user);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Handle messages from the embedded Expo app
  useEffect(() => {
    const handleMessage = (event) => {
      // Ensure the message is from your Expo app domain
      if (event.origin !== 'https://your-expo-app-domain.com') {
        return;
      }

      if (event.data.type === 'AUTH_REQUIRED') {
        // Expo app is requesting authentication
        if (!user) {
          // Redirect to login if not authenticated
          window.location.href = '/login';
        }
      }

      if (event.data.type === 'NAVIGATE') {
        // Handle navigation requests from Expo app
        if (event.data.path) {
          window.location.href = event.data.path;
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [user]);

  if (loading) {
    return (
      <div className="expo-app-page">
        <div className="loading-container">
          <div className="loading-spinner">
            <i className="fas fa-spinner fa-spin"></i>
          </div>
          <h3>Loading LogiCar App...</h3>
          <p>Please wait while we load your mobile app experience.</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="expo-app-page">
        <div className="auth-required">
          <div className="auth-content">
            <i className="fas fa-lock"></i>
            <h3>Authentication Required</h3>
            <p>Please log in to access the LogiCar mobile app.</p>
            <button 
              onClick={() => window.location.href = '/login'}
              className="login-btn"
            >
              <i className="fas fa-sign-in-alt"></i>
              Login to Continue
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Build the Expo app URL with authentication
  const expoAppUrl = `https://your-expo-app-domain.com?auth=true&uid=${user.uid}&email=${encodeURIComponent(user.email)}&name=${encodeURIComponent(user.displayName || '')}`;

  return (
    <div className="expo-app-page">
      <SEO 
        title="LogiCar Mobile App - Vehicle Maintenance Dashboard"
        description="Access your LogiCar mobile app directly in your browser. Manage your vehicle maintenance with our comprehensive mobile experience."
        canonical="https://www.logicarapp.com/app"
      />
      
      {!hideHeader && (
        <div className="app-header">
          <div className="app-title">
            <span>LogiCar</span>
          </div>
          <div className="app-actions">
            <button 
              onClick={() => setHideHeader(true)} 
              className="action-btn fullscreen"
              title="Fullscreen"
            >
              <i className="fas fa-expand"></i>
            </button>
            <button 
              onClick={() => auth.signOut()} 
              className="action-btn logout"
              title="Logout"
            >
              <i className="fas fa-sign-out-alt"></i>
            </button>
          </div>
        </div>
      )}
      
      {hideHeader && (
        <div className="header-toggle">
          <button 
            onClick={() => setHideHeader(false)} 
            className="show-header-btn"
            title="Show Header"
          >
            <i className="fas fa-compress"></i>
          </button>
        </div>
      )}

      <div className="app-container">
        <iframe
          src={expoAppUrl}
          width="100%"
          height="100%"
          frameBorder="0"
          allow="camera; microphone; geolocation; clipboard-read; clipboard-write; payment; usb; serial"
          sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-modals allow-downloads"
          title="LogiCar Mobile App"
          className="expo-iframe"
          onLoad={() => {
            // Send user data to the Expo app
            const iframe = document.querySelector('.expo-iframe');
            if (iframe && iframe.contentWindow) {
              iframe.contentWindow.postMessage({
                type: 'USER_DATA',
                user: {
                  uid: user.uid,
                  email: user.email,
                  displayName: user.displayName,
                  photoURL: user.photoURL
                }
              }, '*');
            }
          }}
        />
      </div>
    </div>
  );
};

export default ExpoApp;
